import { useEffect, useMemo, useRef } from 'react'

import { F0Box } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import Popover from 'modules/job_catalog/components/Popover'
import SalaryPopoverHeader from 'modules/job_catalog/benchmark/components/SalaryPopoverHeader'
import SalaryPopoverBody from 'modules/job_catalog/benchmark/components/SalaryPopoverBody'
import { computeMarketPosition } from 'modules/job_catalog/benchmark/lib/marketPosition'
import type { BenchmarkMatchResult, MarketPosition } from 'modules/job_catalog/benchmark/types'
import {
  defaultCurrency,
  formatSalaryFromCents,
  getSalaryFrequencyText,
} from 'modules/job_catalog/lib/salaryUtils'

const getStatusTheme = (position: MarketPosition): { bg: string } => {
  switch (position) {
    case 'above':
      return { bg: 'color-mix(in srgb, hsl(var(--info-50)) 10%, white)' }
    case 'below':
      return { bg: 'color-mix(in srgb, hsl(var(--warning-50)) 8%, white)' }
    case 'at_market':
      return { bg: 'color-mix(in srgb, hsl(var(--positive-50)) 8%, white)' }
    default:
      return { bg: 'color-mix(in srgb, hsl(var(--neutral-solid-50)) 6%, white)' }
  }
}

interface Props {
  nodeName: string
  roleName?: string
  familyName?: string
  functionName?: string
  workplaceName?: string
  isOpen: boolean
  onClose: () => void
  anchorElement?: HTMLElement | null
  anchorHorizontalInset?: number
  benchmark: BenchmarkMatchResult
  mappingType?: 'manual' | 'auto' | null
  from: string
}

const SalaryPopover = ({
  nodeName,
  roleName,
  familyName,
  functionName,
  workplaceName,
  isOpen,
  onClose,
  anchorElement,
  anchorHorizontalInset,
  benchmark,
  mappingType,
  from,
}: Props) => {
  const hasData = benchmark.status === 'matched'
  const isSuppressed = benchmark.status === 'suppressed'
  const isUnmapped = benchmark.status === 'unmapped'
  const isNoInfo = !hasData && !isSuppressed && !isUnmapped

  const companyMedian = useMemo(() => {
    if (!benchmark.companySalaryRange) return null
    if (benchmark.companySalaryRange.gross != null) return benchmark.companySalaryRange.gross
    if (benchmark.companySalaryRange.min != null && benchmark.companySalaryRange.max != null) {
      return (benchmark.companySalaryRange.min + benchmark.companySalaryRange.max) / 2
    }
    return null
  }, [benchmark.companySalaryRange])

  const marketReferenceSalary = benchmark.tgaP50 ?? null

  const position = computeMarketPosition(benchmark)

  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      track(trackingEvents.jobCatalog.openBenchmarkPopoverSubmit, {
        event_category: 'Organizations',
        from,
        benchmark_status: benchmark.status,
        market_position: hasData ? position : 'unknown',
      })
    }
    wasOpenRef.current = isOpen
  }, [isOpen, from, benchmark.status, hasData, position])

  const marketDelta = useMemo(() => {
    if (companyMedian == null || benchmark.tgaP50 == null || benchmark.tgaP50 === 0) return null
    return Math.round((Math.abs(companyMedian - benchmark.tgaP50) / benchmark.tgaP50) * 100)
  }, [benchmark.tgaP50, companyMedian])

  const statusTheme = getStatusTheme(hasData ? position : 'unknown')

  const salaryDisplay = useMemo(() => {
    const currency = benchmark.currency ?? benchmark.companySalaryRange?.currency ?? defaultCurrency
    const periodicity = getSalaryFrequencyText(undefined, {
      singular: true,
    })
    if (marketReferenceSalary == null) return { amount: '--', periodicity: `/ ${periodicity}` }
    return {
      amount: formatSalaryFromCents(marketReferenceSalary, undefined, currency),
      periodicity: `/ ${periodicity}`,
    }
  }, [benchmark.companySalaryRange, benchmark.currency, marketReferenceSalary])

  const headerTitle =
    isNoInfo || isUnmapped
      ? i18n.t('job_catalog.benchmark.states.no_data.title')
      : isSuppressed
        ? i18n.t('job_catalog.benchmark.states.suppressed.title')
        : undefined

  const bodyStatus: 'hasData' | 'suppressed' | 'noInfo' = hasData
    ? 'hasData'
    : isSuppressed
      ? 'suppressed'
      : 'noInfo'

  const cardWidth = { width: 392 }

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={String(i18n.t('job_catalog.benchmark.open_details'))}
      anchorElement={anchorElement}
      anchorHorizontalInset={anchorHorizontalInset}
    >
      <F0Box display="flex" flexDirection="column" gap="lg">
        {/* eslint-disable-next-line factorial/no-raw-html -- Card with exact background color and shadow not available via F0Box tokens */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            ...cardWidth,
            borderRadius: 16,
            overflow: 'hidden',
            position: 'relative',
            background: statusTheme.bg,
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
          }}
        >
          <SalaryPopoverHeader
            position={hasData ? position : 'unknown'}
            marketDelta={hasData ? marketDelta : undefined}
            title={headerTitle}
            onClose={onClose}
          />
          {/* eslint-disable-next-line factorial/no-raw-html -- Negative margin for overlap effect not available via F0Box */}
          <div style={{ marginTop: -28 }}>
            <SalaryPopoverBody
              status={bodyStatus}
              benchmark={benchmark}
              levelName={nodeName}
              roleName={roleName}
              familyName={familyName}
              functionName={functionName}
              workplaceName={workplaceName}
              salaryDisplay={salaryDisplay}
              mappingType={mappingType}
              from={from}
            />
          </div>
        </div>
      </F0Box>
    </Popover>
  )
}

export default SalaryPopover
